import React from 'react';
import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
const negatives = ['This is not a generic firefighter ebook.', 'This is not a collection of random training notes.', 'This is not another text-heavy manual that takes hours to turn into a lesson.', 'And it is not a disconnected set of resources you have to organize yourself.'];
const teachingFlow = ['OPEN THE ATLAS.', 'CHOOSE THE TOPIC.', 'SHOW THE VISUAL.', 'EXPLAIN THE CONCEPT.', 'RUN THE TRAINING.', 'ASSESS THE LEARNING.'];
const journey = [{
  step: 'FOUNDATIONS',
  detail: 'Understand firefighter equipment, protective systems, tools and essential readiness.'
}, {
  step: 'FIREGROUND OPERATIONS',
  detail: 'Explain hose systems, water supply, nozzles and fireground organization.'
}, {
  step: 'BUILDING ACCESS',
  detail: 'Teach ladders, forcible-entry concepts, ventilation awareness and building access.'
}, {
  step: 'SEARCH & RESCUE',
  detail: 'Develop knowledge of search organization, rescue priorities, structural fire conditions and team coordination.'
}, {
  step: 'TECHNICAL INCIDENTS',
  detail: 'Explore vehicle rescue, technical incident awareness, rescue zones and specialized hazards.'
}, {
  step: 'INSTRUCTOR DEVELOPMENT',
  detail: 'Organize drills, training scenarios, assessments, student progressions and instructional decision-making tools.'
}];
const ease = [0.23, 1, 0.32, 1] as const;
export function SolutionReveal() {
  return <section className="py-20 px-4 bg-red-50 relative overflow-hidden" data-id="element-267" data-mp="1x72m5oiio8v7">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-3xl h-1 bg-red-700" data-id="element-268" data-mp="224unta8yr9cl" />

      <div className="max-w-4xl mx-auto text-center" data-id="element-269" data-mp="26xnrrvwlydnm">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.3,
        ease
      }} data-id="element-270" data-mp="19h8aha9xoqcj">
          <p className="text-sm md:text-base font-semibold text-neutral-900/80 mb-4" data-id="element-271" data-mp="2hr8tj1n43cjy">
            The Complete Visual Firefighter Training System for Professional Instructors
          </p>
          <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4 text-neutral-900" data-id="element-272" data-mp="2cqbbcp23qiig">
            FIRE SERVICE INSTRUCTOR <span className="text-red-700" data-id="element-273" data-mp="2mhfhcz9sut15">ATLAS</span>
          </h2>
          <p className="text-base md:text-lg text-neutral-900 font-semibold mb-1" data-id="element-274" data-mp="2mkbewmhqjfp2">
            A Complete 6-in-1 Visual Training & Reference System
          </p>
          <p className="text-base md:text-lg text-neutral-800/75 mb-10" data-id="element-275" data-mp="1tul86yifng2z">
            6 Professional Volumes · Equipment · Fireground Operations · Search & Rescue · Technical
            Incidents · Instructor Training · 5 Exclusive Bonuses
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 text-left" data-id="element-276" data-mp="3j7f0torhnbyi">
          <motion.div initial={{
          opacity: 0,
          x: -12
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease
        }} className="space-y-3 bg-white/60 p-6 rounded-2xl border border-neutral-300" data-id="element-277" data-mp="3ja17lm6k1nfo">
            {negatives.map(statement => <div key={statement} className="flex items-start gap-3" data-id="element-278" data-mp="3cszne4md9tfv">
                <X className="text-neutral-900/50 shrink-0 mt-1" size={18} data-id="element-279" data-mp="3hqswb5f211ju" />
                <p className="text-neutral-800/80 text-sm" data-id="element-280" data-mp="3hsvr02r5jiuw">{statement}</p>
              </div>)}
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 12
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.3,
          ease
        }} className="bg-white p-6 rounded-2xl border-2 border-red-700" data-id="element-281" data-mp="eau20gtjzrhi">
            <div className="flex items-start gap-3" data-id="element-282" data-mp="sad9d7ejiysr">
              <Check className="text-red-700 shrink-0 mt-1" size={20} data-id="element-283" data-mp="s8ilplbvwskz" />
              <div className="space-y-3" data-id="element-284" data-mp="x1vbhysav5fb">
                <p className="text-neutral-900 font-medium text-base leading-relaxed" data-id="element-285" data-mp="x2wow9wsg407">
                  Fire Service Instructor Atlas is a complete visual training and reference system
                  created for fire service professionals responsible for educating firefighters.
                </p>
                <p className="text-neutral-800 text-sm leading-relaxed" data-id="element-286" data-mp="2vljm31yy171m">
                  Whether you&apos;re preparing new recruits, developing active firefighters,
                  organizing department training or creating academy lessons... the system brings
                  six essential areas of firefighter education together in one organized resource.
                </p>
                <p className="text-neutral-900 font-semibold text-sm leading-relaxed" data-id="element-287" data-mp="3egq8hf4p7r4n">
                  Instead of starting every week with an empty lesson plan, you have visual teaching
                  materials, organized topics, training scenarios and instructor support tools
                  available in one place.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mt-10" data-id="element-288" data-mp="dga1ymfq3mw8">
          <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1.5" data-id="element-289" data-mp="icgzbqu1zq2k">
            {teachingFlow.map(line => <li key={line} className="text-lg md:text-xl font-extrabold tracking-wide text-red-700" data-id="element-290" data-mp="naij6dzaztf3">
                {line}
              </li>)}
          </ul>
          <p className="mt-4 text-xs text-neutral-800/60" data-id="element-291" data-mp="wdjk809baubw">
            Practical activities must be conducted under qualified supervision and applicable
            department procedures.
          </p>
        </div>

        <div className="mt-12" data-id="element-292" data-mp="163w4rsh4mni1">
          <p className="text-neutral-900/80 text-sm font-semibold uppercase tracking-widest mb-5" data-id="element-293" data-mp="164xhe1dsji1v">
            The System Follows One Complete Training Journey
          </p>
          <ol className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" data-id="element-294" data-mp="1g2bzs6j9ygwt">
            {journey.map(item => <li key={item.step} className="flex flex-col items-center px-3 py-4 bg-white rounded-xl border border-neutral-200" data-id="element-295" data-mp="1k09prbkvd719">
                <span className="text-2xl md:text-3xl font-extrabold tracking-tight leading-tight text-red-700" data-id="element-296" data-mp="3c5gq1iuner59">
                  {item.step}
                </span>
                <span className="text-sm text-neutral-800/80 mt-1" data-id="element-297" data-mp="3l4btin6b5n4v">{item.detail}</span>
              </li>)}
          </ol>
          <p className="mt-8 text-sm md:text-base font-bold tracking-widest text-neutral-900" data-id="element-298" data-mp="3mivzg0yp7kv">
            FOUNDATIONS → FIREGROUND → ACCESS → RESCUE → TECHNICAL INCIDENTS → INSTRUCTOR DEVELOPMENT
          </p>
        </div>
      </div>
    </section>;
}